/**
 * Number Formatting Utilities
 *
 * Standardized number formatting across the consumer portal.
 * All decimal numbers should use these utilities for consistency.
 *
 * @see https://github.com/leanopx/ops-matrix/issues/150
 */

/**
 * Display configuration for number formatting.
 * Centralized settings that can be updated in one place.
 */
export const DISPLAY_CONFIG = {
  /** Default number of decimal places for general numbers */
  decimalPlaces: 2,
  /** Decimal places for currency/cost displays (more precision needed) */
  currencyDecimalPlaces: 4,
  /** Decimal places for percentages */
  percentDecimalPlaces: 1,
  /** Decimal places for file sizes */
  fileSizeDecimalPlaces: 1,
} as const;

export interface FormatNumberOptions {
  /** Number of decimal places (default: DISPLAY_CONFIG.decimalPlaces) */
  decimals?: number;
  /** Fallback string for null/undefined/NaN values (default: '-') */
  fallback?: string;
  /** Whether to use locale-aware formatting with thousand separators (default: false) */
  useLocale?: boolean;
  /** Locale to use for formatting (default: 'en-US') */
  locale?: string;
}

/**
 * Format a number to a specified number of decimal places.
 *
 * Handles edge cases like null, undefined, NaN, and Infinity.
 *
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted string representation of the number
 *
 * @example
 * formatNumber(1791.333333337) // '1791.33'
 * formatNumber(null) // '-'
 * formatNumber(3.14159, { decimals: 4 }) // '3.1416'
 * formatNumber(1234567.89, { useLocale: true }) // '1,234,567.89'
 */
export function formatNumber(value: number | null | undefined, options?: FormatNumberOptions): string {
  const {
    decimals = DISPLAY_CONFIG.decimalPlaces,
    fallback = '-',
    useLocale = false,
    locale = 'en-US',
  } = options ?? {};

  if (value == null || !Number.isFinite(value)) {
    return fallback;
  }

  if (useLocale) {
    return value.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  return value.toFixed(decimals);
}

/**
 * Format a number as a percentage.
 *
 * @param value - The number to format (0-1 for fractional, or already multiplied by 100)
 * @param options - Formatting options (decimals defaults to 1 for percentages)
 * @param isFractional - If true, multiplies by 100 first (default: false)
 * @returns Formatted percentage string with % suffix
 *
 * @example
 * formatPercent(85.5) // '85.5%'
 * formatPercent(0.855, {}, true) // '85.5%'
 * formatPercent(85.567, { decimals: 2 }) // '85.57%'
 */
export function formatPercent(
  value: number | null | undefined,
  options?: FormatNumberOptions,
  isFractional = false,
): string {
  const { decimals = DISPLAY_CONFIG.percentDecimalPlaces, fallback = '-', ...rest } = options ?? {};

  if (value == null || !Number.isFinite(value)) {
    return fallback;
  }

  const displayValue = isFractional ? value * 100 : value;
  return `${formatNumber(displayValue, { decimals, fallback, ...rest })}%`;
}

/**
 * Format a number as currency.
 *
 * @param value - The number to format
 * @param options - Formatting options (decimals defaults to 4 for currency precision)
 * @param currencySymbol - Currency symbol to prepend (default: '$')
 * @returns Formatted currency string
 *
 * @example
 * formatCurrency(1234.5678) // '$1234.5678'
 * formatCurrency(1234.5678, { decimals: 2 }) // '$1234.57'
 * formatCurrency(1234.5678, { useLocale: true }) // '$1,234.5678'
 */
export function formatCurrency(
  value: number | null | undefined,
  options?: FormatNumberOptions,
  currencySymbol = '$',
): string {
  const { decimals = DISPLAY_CONFIG.currencyDecimalPlaces, fallback = '-', ...rest } = options ?? {};

  if (value == null || !Number.isFinite(value)) {
    return fallback;
  }

  return `${currencySymbol}${formatNumber(value, { decimals, fallback, ...rest })}`;
}

/**
 * Format bytes into human-readable file size.
 *
 * @param bytes - The number of bytes
 * @param options - Formatting options (decimals defaults to 1 for file sizes)
 * @returns Formatted file size string (e.g., '1.5 MB')
 *
 * @example
 * formatFileSize(1024) // '1.0 KB'
 * formatFileSize(1048576) // '1.0 MB'
 * formatFileSize(1073741824) // '1.0 GB'
 */
export function formatFileSize(bytes: number | null | undefined, options?: FormatNumberOptions): string {
  const { decimals = DISPLAY_CONFIG.fileSizeDecimalPlaces, fallback = '-' } = options ?? {};

  if (bytes == null || !Number.isFinite(bytes)) {
    return fallback;
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(decimals)} KB`;
  }

  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(decimals)} MB`;
  }

  return `${(bytes / (1024 * 1024 * 1024)).toFixed(decimals)} GB`;
}

/**
 * Format a duration in milliseconds to human-readable format.
 *
 * @param ms - Duration in milliseconds
 * @param options - Formatting options
 * @returns Formatted duration string (e.g., '150ms', '1.50s')
 *
 * @example
 * formatDuration(150) // '150ms'
 * formatDuration(1500) // '1.50s'
 * formatDuration(65000) // '1.08m'
 */
export function formatDuration(ms: number | null | undefined, options?: FormatNumberOptions): string {
  const { decimals = DISPLAY_CONFIG.decimalPlaces, fallback = '-' } = options ?? {};

  if (ms == null || !Number.isFinite(ms)) {
    return fallback;
  }

  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }

  if (ms < 60000) {
    return `${(ms / 1000).toFixed(decimals)}s`;
  }

  return `${(ms / 60000).toFixed(decimals)}m`;
}

/**
 * Format a large number with K/M/B suffixes.
 *
 * @param value - The number to format
 * @param options - Formatting options
 * @returns Formatted string with suffix (e.g., '1.5K', '2.3M')
 *
 * @example
 * formatCompact(1500) // '1.50K'
 * formatCompact(1500000) // '1.50M'
 * formatCompact(1500000000) // '1.50B'
 */
export function formatCompact(value: number | null | undefined, options?: FormatNumberOptions): string {
  const { decimals = DISPLAY_CONFIG.decimalPlaces, fallback = '-' } = options ?? {};

  if (value == null || !Number.isFinite(value)) {
    return fallback;
  }

  if (Math.abs(value) < 1000) {
    return value.toFixed(decimals);
  }

  if (Math.abs(value) < 1000000) {
    return `${(value / 1000).toFixed(decimals)}K`;
  }

  if (Math.abs(value) < 1000000000) {
    return `${(value / 1000000).toFixed(decimals)}M`;
  }

  return `${(value / 1000000000).toFixed(decimals)}B`;
}

/**
 * Format a document status for display.
 *
 * Converts snake_case status strings to uppercase display format.
 * Handles special cases like 'not_required' which displays as 'Not Required'.
 *
 * @param status - The document status string
 * @returns Formatted status string for display
 *
 * @example
 * formatDocumentStatus('pending') // 'PENDING'
 * formatDocumentStatus('rejected') // 'REJECTED'
 * formatDocumentStatus('not_required') // 'Not Required'
 * formatDocumentStatus('in_review') // 'IN REVIEW'
 */
export function formatDocumentStatus(status: string): string {
  if (status === 'not_required') {
    return 'Not Required';
  }
  return status.replace(/_/g, ' ').toUpperCase();
}
